// Pure helpers for the per-shop product tile order.
//
// A layout is `{ "<category_id>": [product_id, ...] }`. Keys are always read
// as strings (JSON), category ids in Redux are numbers — layoutFor bridges.

export const layoutFor = (layout, categoryId) => {
  const ids = layout?.[String(categoryId)];
  return Array.isArray(ids) ? ids : [];
};

export const isLayout = (value) =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.values(value).every(Array.isArray);

export const isEmptyLayout = (layout) => Object.keys(layout ?? {}).length === 0;

// Products listed in `ids` first (in that order, ignoring unknown ids and
// duplicates), then every other product in the order the API returned them.
// Returns the very same array when there is nothing to apply, so selectors
// keep a stable reference.
export const orderProducts = (products, ids) => {
  const list = Array.isArray(products) ? products : [];
  if (!Array.isArray(ids) || ids.length === 0) return list;

  const byId = new Map(list.map((product) => [product.id, product]));
  const seen = new Set();
  const ordered = [];
  ids.forEach((id) => {
    const product = byId.get(id);
    if (product && !seen.has(id)) {
      seen.add(id);
      ordered.push(product);
    }
  });
  list.forEach((product) => {
    if (!seen.has(product.id)) ordered.push(product);
  });
  return ordered;
};

// Moves `activeId` to the slot currently occupied by `overId` (dnd-kit
// semantics: the dragged item takes the position of the item it was dropped
// on, the others shift). Same array back when nothing changes.
export const moveId = (ids, activeId, overId) => {
  const from = ids.indexOf(activeId);
  const to = ids.indexOf(overId);
  if (from === -1 || to === -1 || from === to) return ids;
  const next = ids.slice();
  next.splice(from, 1);
  next.splice(to, 0, activeId);
  return next;
};

export const localLayoutKey = (shopId) => `wosh.pos.productLayout.${shopId}`;

export const readLocalLayout = (shopId) => {
  try {
    const raw = localStorage.getItem(localLayoutKey(shopId));
    const parsed = raw ? JSON.parse(raw) : null;
    return isLayout(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
};

export const writeLocalLayout = (shopId, layout) => {
  try {
    localStorage.setItem(localLayoutKey(shopId), JSON.stringify(layout));
    return true;
  } catch (_) {
    return false;
  }
};

export const clearLocalLayout = (shopId) => {
  try {
    localStorage.removeItem(localLayoutKey(shopId));
  } catch (_) {
    // nothing to do: a storage we cannot touch holds nothing we rely on
  }
};
